import { useEffect, useState, type ReactNode } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { getFullCard } from '../api/cardCache';
import CardImage from './CardImage';
import { cardImageUrl } from '../config';
import type { Card, SynergyEntry } from '../../../shared/types';

/** The preview's main card art, draggable into a deck zone (from='popup'). Must
 *  be rendered inside a DndContext. */
function DraggableCardImg({ name, children }: { name: string; children: ReactNode }) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: `popup:${name}`, data: { name, from: 'popup' } });
  return (
    <div
      ref={setNodeRef} {...listeners} {...attributes}
      // Cancel the browser's native image drag so the dnd-kit pointer sensor wins.
      onDragStart={e => e.preventDefault()}
      draggable={false}
      className={`cardprev-img cardprev-img-drag${isDragging ? ' dragging' : ''}`}
      style={{ cursor: 'grab', touchAction: 'none' }}>
      {children}
    </div>
  );
}

/** A synergy tile: click swaps the preview to it; drag adds it to a deck zone
 *  (from='popup'). Rendered inside the deck-editor popup (a DndContext). */
function SynCard({ entry, onPick }: { entry: SynergyEntry; onPick?: (name: string) => void }) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: `popupsyn:${entry.name}`, data: { name: entry.name, from: 'popup' } });
  return (
    <button
      type="button" ref={setNodeRef} {...listeners} {...attributes}
      onDragStart={e => e.preventDefault()} draggable={false}
      className={`syn-card${isDragging ? ' dragging' : ''}`}
      onClick={() => onPick?.(entry.name)}
      style={{ touchAction: 'none' }}
      title={`Appears together in ${entry.coDecks} deck${entry.coDecks === 1 ? '' : 's'}`}
    >
      <div className="syn-card-img">
        <CardImage src={cardImageUrl(entry.image)} alt={entry.name} />
        <span className="syn-card-score">{entry.score}%</span>
      </div>
      <span className="syn-card-name">{entry.name}</span>
    </button>
  );
}

/** A compact synergy list (mini art + name + %). */
function SynergyRow({ title, entries, onPick }: { title: string; entries: SynergyEntry[]; onPick?: (name: string) => void }) {
  if (!entries || entries.length === 0) return null;
  return (
    <section className="synergy cardprev-syn">
      <h3>{title}</h3>
      <div className="syn-grid">
        {entries.map(e => <SynCard key={e.name} entry={e} onPick={onPick} />)}
      </div>
    </section>
  );
}

/** Readable card preview (large art + text) for the deck builder — lets you
 *  read a card without leaving the page. Fetches the full card (cached).
 *  With `showSynergy`, also lists the card's Skill + card synergies. */
export default function CardPreview({ name, showSynergy = false, onPickSynergy, draggableCard = false }: {
  name: string | null; showSynergy?: boolean; onPickSynergy?: (name: string) => void; draggableCard?: boolean;
}) {
  const [card, setCard] = useState<Card | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCard(null);
    if (name) getFullCard(name).then(c => { if (!cancelled) setCard(c); });
    return () => { cancelled = true; };
  }, [name]);

  if (!name) return <div className="cardprev cardprev-empty muted">Hover or tap a card to preview it.</div>;
  if (!card) return <div className="cardprev"><p className="muted">Loading…</p></div>;

  const stat = (v: number | null, raw?: string | null) => (v != null ? v : raw === '?' ? '?' : '—');
  const imgEl = <CardImage src={cardImageUrl(card.primaryImage)} alt={card.name} />;
  const main = (
    <div className="cardprev">
      {draggableCard
        ? <DraggableCardImg name={card.name}>{imgEl}</DraggableCardImg>
        : <div className="cardprev-img">{imgEl}</div>}
      <div className="cardprev-body">
        <div className="cardprev-name">{card.nameDisplay ?? card.name}</div>
        <div className="cardprev-badges">
          <span className="badge">{card.category}</span>
          {card.attribute && <span className="badge">{card.attribute}</span>}
          {card.level != null && <span className="badge">Lv {card.level}</span>}
        </div>
        {card.types && card.types.length > 0 && (
          <div className="cardprev-type">[ {card.types.join(' / ')} ]</div>
        )}
        {card.category === 'Monster' && (
          <div className="cardprev-stats">
            ATK {stat(card.atk, card.printings[0]?.atkRaw)} / DEF {stat(card.def, card.printings[0]?.defRaw)}
          </div>
        )}
        {card.effect && <div className="cardprev-effect">{card.effect}</div>}
      </div>
    </div>
  );

  if (!showSynergy || (card.topSkills.length === 0 && card.topSynergy.length === 0)) return main;
  return (
    <div className="cardprev-with-syn">
      {main}
      <SynergyRow title="Top Skills" entries={card.topSkills} onPick={onPickSynergy} />
      <SynergyRow title="Top Synergy" entries={card.topSynergy} onPick={onPickSynergy} />
    </div>
  );
}
